import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import {
  Link,
  Checkbox,
  Chip,
  TextField,
  Typography, Grid,
} from '@material-ui/core';
import { Autocomplete, RenderInputParams } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { FieldArray, FieldArrayRenderProps, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@material-ui/icons/Search';
import { find } from 'lodash';

import { useCurrentUser, useFollowed } from 'hooks';
import { UserGroup } from 'types/app';
import { ContentInput, Group } from 'api';
import { GroupAvatar, UserAvatar } from 'components';

import { USER_FEED_ID } from '../../../constants';

const useStyles = makeStyles(theme => ({
  chip: {
    border: 'none',
    height: 'inherit',
    maxWidth: '170px',
    fontSize: 'inherit'
  },
  chipLabel: {
    padding: theme.spacing(0, .5, 0, 1.5)
  },
  doneButton: {
    padding: theme.spacing(0, .5),

    '&:hover': {
      cursor: 'pointer'
    }
  },
  inputIcon: {
    margin: theme.spacing(0, 1)
  },
  groupAvatar: {
    margin: theme.spacing(0)
  },
}));

const useAutoCompleteStyles = makeStyles(theme => ({
  paper: {
    boxShadow: 'none',
    margin: 0,
  },
  inputRoot: {
    backgroundColor: theme.palette.background.paper,
    paddingRight: theme.spacing(0),
    cursor: 'pointer'
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 3, 0.5, 1),

    '&:hover, &[aria-selected="true"]': {
      background: 'transparent'
    },
  },
  listbox: {
    margin: 0,
    padding: 0
  },
}))

const GroupSelect: FC = () => {
  const classes = useStyles();
  const autocompleteClasses = useAutoCompleteStyles();
  const { t } = useTranslation();
  const user = useCurrentUser();
  const { values, errors, touched } = useFormikContext<ContentInput>();
  const { postable } = useFollowed();

  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedGroups, setSelectedGroups] = useState([]);

  const { list } = postable; // list of groups
  const { group_ids } = values; // list of selected groups with id
  // const selectedGroups = getSelectedGroups();

  useEffect(() => {
    setSelectedGroups(getSelectedGroups())
  }, [group_ids])

  function getSelectedGroups() {
    return group_ids.map(id => list.find(item => item.id === id));
  }

  function getError() {
    return !!touched.group_ids ? errors.group_ids : null;
  }

  function getOptions() {
    return [...list].sort((a, b) => {
      let a1 = selectedGroups.indexOf(a)
      let b1 = selectedGroups.indexOf(b)

      a1 = a1 === -1 ? selectedGroups.length + list.indexOf(a) : a1;
      b1 = b1 === -1 ? selectedGroups.length + list.indexOf(b) : b1;
      return a1 - b1;
    })
  }

  function onClickToggle() {
    setIsOpen(!isOpen);
    setInputValue('');
  }

  function onChangeInput(event: ChangeEvent<HTMLInputElement>) {
    setInputValue(event.target.value);
  }

  function onClickGroupItem(group: UserGroup, helpers: FieldArrayRenderProps) {
    const currentIndex = group_ids.findIndex(id => id === group.id);

    currentIndex >= 0 ? helpers.remove(currentIndex) : helpers.push(group.id);
  }

  function renderAvatar(group: UserGroup) {
    return group.id === USER_FEED_ID ? (
      <UserAvatar user={user} />
    ) : (
      <GroupAvatar group={group as Group} className={classes.groupAvatar}/>
    );
  }

  function renderOptions(group: UserGroup, helpers: FieldArrayRenderProps) {
    const isSelected = group_ids.includes(group.id);

    return (
      <Grid
        container
        alignItems="center"
        key={group.id}
        onClick={() => onClickGroupItem(group, helpers)}
      >
        <Grid item xs={2}>
          {renderAvatar(group)}
        </Grid>
        <Grid item xs={9}>
          <Typography noWrap variant={isSelected ? "body2" : "body1"}>{group.title}</Typography>
        </Grid>
        <Grid item xs={1}>
          <Checkbox checked={isSelected} color="primary" />
        </Grid>
      </Grid>
    )
  }

  function renderInput(params: RenderInputParams) {
    let startAdornment = params.InputProps.startAdornment;
    let endAdornment = params.InputProps.endAdornment;
    if (isOpen) {
      startAdornment = (
        <SearchIcon className={classes.inputIcon} fontSize="small"/>
      )

      endAdornment = (
        <Link
          className={classes.doneButton}
          color="primary"
          underline="none"
          onClick={onClickToggle}>
          {t('common:done')}
        </Link>
      )
    }

    function getPlaceholder() {
      const myTimeline = find(selectedGroups, { id: "0" });
      const isOnlyTimeline = selectedGroups.length === 1 && myTimeline;

      if (isOpen) {
        return t('posting:searchGroups');
      }

      if (selectedGroups.length > 1) {
        return '';
      }

      if (isOnlyTimeline) {
        return t('posting:selectGroup');
      }

      if (selectedGroups.length === 1) {
        return '';
      }

      return t('posting:selectGroup');
    }

    return (
      <TextField
        {...params}
        value={inputValue}
        variant="outlined"
        placeholder={getPlaceholder()}
        error={!!getError()}
        helperText={getError()}
        onChange={onChangeInput}
        InputProps={{
          ...params.InputProps,
          startAdornment,
          endAdornment,
        }}
      />
    );
  }

  function renderTags(value: UserGroup[]) {
    const myTimeline = find(value, { id: "0" });

    if (value.length === 0 || (value.length === 1 && myTimeline)) {
      return [];
    }

    return value.filter(item => item !== myTimeline).map(group => (
      <Chip
        key={group.id}
        variant="outlined"
        classes={{ label: classes.chipLabel, root: classes.chip }}
        label={<Typography variant="body2">{group.title}</Typography>}
      />
    ));
  }

  return (
    <FieldArray name="group_ids">
      {helpers => (
        <Autocomplete
          classes={autocompleteClasses}
          open={isOpen}
          onOpen={onClickToggle}
          onClose={onClickToggle}
          openOnFocus
          multiple
          fullWidth
          disableClearable
          disableCloseOnSelect
          forcePopupIcon={!isOpen}
          size="small"
          options={getOptions()}
          getOptionLabel={(group) => group.title}
          renderOption={(group) => renderOptions(group, helpers)}
          renderInput={renderInput}
          renderTags={renderTags}
          limitTags={1}
          value={selectedGroups}
          inputValue={inputValue}
        />
      )}
    </FieldArray>
  )
}

export default GroupSelect;